import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { has } from 'lodash';
import { FORM_TYPE_CREATE_DATA_SOURCE } from 'js/constants/formTypes';
import { CONNECTOR_CODE_SQL_RUNNER } from 'js/constants/connectionCategories';
import { PROBE_GET_TREE_REQUEST, PROBE_GET_SAMPLE_REQUEST } from 'js/constants/actionTypes';
import { getCredentialsByType } from 'js/reducers/selectors';

const FORM_TYPE = FORM_TYPE_CREATE_DATA_SOURCE;
const SELECTED_CONNECTION = CONNECTOR_CODE_SQL_RUNNER;

export interface SqlRunnerFormState {
  paging: Record<string, unknown>;
  vendors: Record<string, unknown>;
  vendorEndpoints: Record<string, unknown>;
  templatizedScriptConfig: Record<string, unknown>;
  selectedConnection: string;
  selectedCredentialId: number | string | null;
  selectedCredentialInfo: Record<string, unknown> | null;
  credentialsByType: Record<string, { items: Record<string, unknown>; sortedIds: number[] }>;
  credentialsById: Record<string, unknown>;
  sourceName: string;
  sourceDescription: string;
  sourceConfig: Record<string, unknown>;
  selectedTable: Record<string, unknown> | null;
  connectionAuthFieldsFromRedux: unknown[];
  selectedTemplate: number | null;
  configureAwaitingDataSource: boolean;
  addedDataSource: { id: number; name?: string; description?: string } | null;
  isFormAwaitingValidation: boolean;
  isNewCredential: boolean;
  probeData: unknown;
  probeDataFlat: Record<string, unknown> | null;
  probeError: unknown;
  probeTreeLastUpdatedAt: unknown;
  probeSample: unknown;
  isLoadingProbeData: boolean;
  isLoadingProbeSample: boolean;
}

type RootState = Record<string, unknown> & {
  forms?: Record<string, { data?: Record<string, unknown>; awaitingValidation?: boolean }>;
  entities?: {
    sources?: Record<string, unknown>;
    credentials?: { byId: Record<string, unknown> };
    vendors?: Record<string, unknown>;
    vendorEndpoints?: Record<string, unknown>;
  };
  paging?: Record<string, unknown>;
  probe?: Record<
    string,
    {
      nestedProbeTree?: unknown;
      tree?: Record<string, unknown>;
      error?: unknown;
      treeLastUpdatedAt?: unknown;
    }
  >;
  request?: Record<string, unknown>;
};

const selectForm = (state: RootState) => state.forms?.[FORM_TYPE];
const selectEntities = (state: RootState) => state.entities;
const selectPaging = (state: RootState) => state.paging;
const selectProbe = (state: RootState) => state.probe ?? {};
const selectRequest = (state: RootState) => state.request ?? {};

const selectSqlRunnerFormState = createSelector(
  [selectForm, selectEntities, selectPaging, getCredentialsByType, selectProbe, selectRequest],
  (form, entities, paging, credentialsByType, probe, request): SqlRunnerFormState => {
    const formData = form?.data ?? {};
    const entitiesSafe = entities ?? {};
    const addedDataSourceId = formData.addedDataSourceId as number | undefined;
    const sources = entitiesSafe.sources ?? {};
    const selectedCredentialId =
      formData.selectedCredentialId ??
      (formData.credentialCreationStatus as { credential?: number })?.credential ??
      null;

    const probeKey = selectedCredentialId != null ? String(selectedCredentialId) : null;
    const probeResponse =
      probeKey != null && has(probe, probeKey)
        ? (
            probe as Record<
              string,
              {
                nestedProbeTree?: unknown;
                tree?: Record<string, unknown>;
                error?: unknown;
                treeLastUpdatedAt?: unknown;
              }
            >
          )[probeKey]
        : null;
    const probeData = probeResponse?.nestedProbeTree ?? null;
    const probeDataFlat = probeResponse?.tree ?? null;
    const probeError = probeResponse?.error ?? null;
    const probeTreeLastUpdatedAt = probeResponse?.treeLastUpdatedAt ?? null;
    const probeSample: unknown = probeResponse ?? null;
    const requestSafe = request as Record<string, unknown>;
    const isLoadingProbeData = !!requestSafe[PROBE_GET_TREE_REQUEST];
    const isLoadingProbeSample = !!requestSafe[PROBE_GET_SAMPLE_REQUEST];

    return {
      paging: (paging ?? {}) as Record<string, unknown>,
      vendors: (entitiesSafe.vendors ?? {}) as Record<string, unknown>,
      vendorEndpoints: (entitiesSafe.vendorEndpoints ?? {}) as Record<string, unknown>,
      templatizedScriptConfig: (formData.templatizedScriptConfig as Record<string, unknown>) ?? {},
      selectedConnection: (formData.selectedConnection as string) ?? SELECTED_CONNECTION,
      selectedCredentialId: selectedCredentialId as number | string | null,
      selectedCredentialInfo: (formData.selectedCredentialInfo as Record<string, unknown>) ?? null,
      credentialsByType,
      credentialsById: (entitiesSafe.credentials ?? {}) as Record<string, unknown>,
      sourceName: (formData.sourceName as string) ?? '',
      sourceDescription: (formData.sourceDescription as string) ?? '',
      sourceConfig: (formData.sourceConfig as Record<string, unknown>) ?? {},
      selectedTable: (formData.selectedTable as Record<string, unknown> | null) ?? null,
      connectionAuthFieldsFromRedux: (formData.connectionAuthFields as unknown[]) ?? [],
      selectedTemplate: (formData.selectedTemplate as number | null) ?? null,
      configureAwaitingDataSource: (formData.configureAwaitingDataSource as boolean) ?? false,
      addedDataSource:
        addedDataSourceId != null && sources[String(addedDataSourceId)]
          ? (sources[String(addedDataSourceId)] as {
              id: number;
              name?: string;
              description?: string;
            })
          : null,
      isFormAwaitingValidation: form?.awaitingValidation ?? false,
      isNewCredential: (formData.isNewCredential as boolean) ?? false,
      probeData,
      probeDataFlat,
      probeError,
      probeTreeLastUpdatedAt,
      probeSample,
      isLoadingProbeData,
      isLoadingProbeSample,
    };
  },
);

export function useSqlRunnerFormState(): SqlRunnerFormState {
  return useSelector(selectSqlRunnerFormState);
}
